// About page editor model (plan R6 AB-1..AB-6). Pure helpers, no React.
//
// Every block and every list item carries a stable client id `_cid` while it
// is on screen: it is the React key and the target of every update, so an
// upload that finishes after a move writes into its own block (or into
// nothing if that block or item was removed), never into the block now at the
// old index. `_cid` never leaves the editor: the save payload, the dirty check
// and the session draft all strip it.
//
// The page is still saved as one document (one Save button), but only with
// the version token it loaded (`expectedUpdatedAt`), never from an editor that
// failed to load, and an empty page over a non-empty one needs a confirmation.

export type AboutSection = {
  type: string;
  enabled?: boolean;
  order?: number;
  data?: Record<string, any>;
  _cid?: string;
};

// The repeatable lists of the block types (the site renders these).
export const LIST_KEYS = ['items', 'steps', 'members'];

let seq = 0;
const salt = Math.random().toString(36).slice(2, 7);
export const newCid = (): string => {
  seq += 1;
  return `c${salt}${seq}`;
};

const isPlainObject = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

// ---- client ids ----------------------------------------------------------------

/**
 * Gives every block and list item a `_cid` (keeping one it already has). List
 * entries that are not objects (a stored `null`) are dropped, as the server
 * normaliser does: they render nothing and would crash the site's list.
 */
export function withCids(sections: readonly AboutSection[]): AboutSection[] {
  return (sections || []).filter(isPlainObject).map((s) => {
    const data: Record<string, any> = isPlainObject(s.data) ? { ...s.data } : {};
    LIST_KEYS.forEach((k) => {
      if (Array.isArray(data[k])) {
        data[k] = data[k].filter(isPlainObject).map((item: Record<string, any>) => ({
          ...item,
          _cid: item._cid || newCid(),
        }));
      }
    });
    return { ...s, data, _cid: s._cid || newCid() };
  });
}

const stripDeep = (v: unknown, depth: number): unknown => {
  if (depth > 4) return v;
  if (Array.isArray(v)) return v.map((x) => stripDeep(x, depth + 1));
  if (isPlainObject(v)) {
    const out: Record<string, unknown> = {};
    Object.keys(v).forEach((k) => {
      if (k !== '_cid') out[k] = stripDeep(v[k], depth + 1);
    });
    return out;
  }
  return v;
};

/** The blocks without any `_cid` key (any depth up to 4). */
export function stripCids(sections: readonly AboutSection[]): AboutSection[] {
  return stripDeep(sections || [], 0) as AboutSection[];
}

/**
 * The saved page (the server's answer, fresh ids) keeps the ids of the blocks
 * and items on screen where they line up (same index, same type), so the
 * editor does not remount everything after a Save.
 */
export function adoptCids(onScreen: readonly AboutSection[], saved: readonly AboutSection[]): AboutSection[] {
  return saved.map((s, i) => {
    const old = onScreen[i];
    if (!old || old.type !== s.type) return s;
    const data: Record<string, any> = { ...(s.data || {}) };
    LIST_KEYS.forEach((k) => {
      const was = old.data?.[k];
      const now = data[k];
      if (Array.isArray(was) && Array.isArray(now) && was.length === now.length) {
        data[k] = now.map((item: Record<string, any>, j: number) => ({ ...item, _cid: was[j]?._cid || item._cid }));
      }
    });
    return { ...s, data, _cid: old._cid || s._cid };
  });
}

// ---- payload, dirty ----------------------------------------------------------------

/** The `sections` of the PATCH body: array order is the display order. */
export function toPayload(sections: readonly AboutSection[]) {
  return stripCids(sections).map((s, i) => ({
    type: s.type,
    enabled: s.enabled !== false,
    order: i,
    data: s.data || {},
  }));
}

/** A comparable snapshot of what a save would store. */
export function snapshotOf(sections: readonly AboutSection[]): string {
  return JSON.stringify(toPayload(sections));
}

export function isDirty(sections: readonly AboutSection[], savedSnapshot: string): boolean {
  return snapshotOf(sections) !== savedSnapshot;
}

/** The GET answer: blocks in display order, and the version token (undefined = an older API without one). */
export function readAnswer(body: any): { sections: AboutSection[]; updatedAt: string | null | undefined } {
  const data = body?.data ?? body;
  const list: AboutSection[] = Array.isArray(data?.sections) ? [...data.sections] : [];
  list.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  const has = isPlainObject(data) && Object.prototype.hasOwnProperty.call(data, 'updatedAt');
  const token = has && (typeof data.updatedAt === 'string' || data.updatedAt === null) ? data.updatedAt : undefined;
  return { sections: withCids(list), updatedAt: token };
}

/**
 * The PATCH body. The token is sent as loaded (a string, or null = "never
 * saved"); when the API gave none (an older API) the key is left out.
 */
export function savePayload(
  sections: readonly AboutSection[],
  updatedAt: string | null | undefined,
  confirmEmpty = false
): Record<string, unknown> {
  const body: Record<string, unknown> = { sections: toPayload(sections) };
  if (updatedAt !== undefined) body.expectedUpdatedAt = updatedAt;
  if (confirmEmpty) body.confirmEmpty = true;
  return body;
}

/** Saving no blocks over a saved non-empty page asks first. */
export function needsEmptyConfirm(sections: readonly AboutSection[], savedSnapshot: string): boolean {
  if (sections.length) return false;
  try {
    const saved = JSON.parse(savedSnapshot);
    return Array.isArray(saved) && saved.length > 0;
  } catch {
    return false;
  }
}

// ---- updates by client id ----------------------------------------------------------

const mapBlock = (
  sections: readonly AboutSection[],
  cid: string,
  fn: (s: AboutSection) => AboutSection
): AboutSection[] => {
  if (!sections.some((s) => s._cid === cid)) return sections as AboutSection[];
  return sections.map((s) => (s._cid === cid ? fn(s) : s));
};

export function moveByCid(sections: readonly AboutSection[], cid: string, dir: -1 | 1): AboutSection[] {
  const i = sections.findIndex((s) => s._cid === cid);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= sections.length) return sections as AboutSection[];
  const next = [...sections];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export function setSectionByCid(
  sections: readonly AboutSection[],
  cid: string,
  patch: Partial<AboutSection>
): AboutSection[] {
  return mapBlock(sections, cid, (s) => ({ ...s, ...patch, _cid: s._cid }));
}

export function setDataByCid(sections: readonly AboutSection[], cid: string, key: string, value: unknown) {
  return mapBlock(sections, cid, (s) => ({ ...s, data: { ...(s.data || {}), [key]: value } }));
}

const listOf = (s: AboutSection, listKey: string): Record<string, any>[] =>
  Array.isArray(s.data?.[listKey]) ? (s.data?.[listKey] as Record<string, any>[]) : [];

/** Writes one field of one list item; a block or item removed meanwhile is a no-op. */
export function setListItemByCid(
  sections: readonly AboutSection[],
  cid: string,
  listKey: string,
  itemCid: string,
  key: string,
  value: unknown
): AboutSection[] {
  const block = sections.find((s) => s._cid === cid);
  if (!block || !listOf(block, listKey).some((it) => it?._cid === itemCid)) return sections as AboutSection[];
  return mapBlock(sections, cid, (s) => ({
    ...s,
    data: {
      ...(s.data || {}),
      [listKey]: listOf(s, listKey).map((it) => (it?._cid === itemCid ? { ...it, [key]: value } : it)),
    },
  }));
}

export function addListItem(sections: readonly AboutSection[], cid: string, listKey: string) {
  return mapBlock(sections, cid, (s) => ({
    ...s,
    data: { ...(s.data || {}), [listKey]: [...listOf(s, listKey), { _cid: newCid() }] },
  }));
}

export function removeListItemByCid(
  sections: readonly AboutSection[],
  cid: string,
  listKey: string,
  itemCid: string
) {
  return mapBlock(sections, cid, (s) => ({
    ...s,
    data: { ...(s.data || {}), [listKey]: listOf(s, listKey).filter((it) => it?._cid !== itemCid) },
  }));
}

export function removeByCid(sections: readonly AboutSection[], cid: string): AboutSection[] {
  return sections.filter((s) => s._cid !== cid);
}

export function addSection(sections: readonly AboutSection[], type: string): AboutSection[] {
  return [...sections, { type, enabled: true, data: {}, _cid: newCid() }];
}

// ---- texts --------------------------------------------------------------------------

export const LOAD_FAILED_TEXT = 'Could not load the About page. Nothing was changed.';
export const CHANGED_TEXT =
  'Someone else saved the About page after you opened it. Your changes were NOT saved.';
export const RELOAD_ADMIN_TEXT =
  'This editor is out of date. Reload the page and make your change again. Nothing was saved.';
export const EMPTY_CONFIRM_TEXT =
  'Remove every block? The site will show its built-in About layout.';
export const LEAVE_TEXT = 'You have unsaved changes. Leave without saving?';
export const UPLOAD_WAIT_TEXT = 'Waiting for the upload…';

export const aboutCode = (err: unknown): string => {
  const c = (err as { code?: unknown } | null)?.code;
  return typeof c === 'string' ? c : '';
};

export const isAboutChanged = (err: unknown) => aboutCode(err) === 'AboutPageChanged';

/** The text of a failed save. */
export function aboutErrorText(err: unknown): string {
  const code = aboutCode(err);
  if (code === 'AboutPageChanged') return CHANGED_TEXT;
  if (code === 'AboutPageReloadAdmin') return RELOAD_ADMIN_TEXT;
  if (code === 'AboutPageEmptyNeedsConfirm') {
    return 'Removing every block needs a confirmation. Nothing was saved.';
  }
  const messages = (err as { messages?: unknown } | null)?.messages;
  if (Array.isArray(messages) && messages.length) return `Not saved: ${messages.join(' · ')}`;
  const message = (err as { message?: unknown } | null)?.message;
  if (typeof message === 'string' && message) return `Not saved: ${message}`;
  return 'Could not save the About page. Nothing was saved.';
}

/** The snackbar after a save (`revalidated` = the site was told to refresh now). */
export const savedText = (revalidated: unknown) =>
  revalidated === true
    ? 'About page saved — it is live on the site now'
    : 'About page saved — the site shows it within a minute';

// ---- session draft (AB-1) ---------------------------------------------------------------

export const DRAFT_KEY = 'kayfa.admin.aboutDraft';

export type AboutDraft = { baseUpdatedAt: string | null; sections: AboutSection[]; savedAt: string };

export function makeDraft(
  baseUpdatedAt: string | null | undefined,
  sections: readonly AboutSection[],
  now: Date = new Date()
): AboutDraft {
  return { baseUpdatedAt: baseUpdatedAt ?? null, sections: toPayload(sections), savedAt: now.toISOString() };
}

export function parseDraft(raw: string | null | undefined): AboutDraft | null {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw);
    if (!isPlainObject(d) || !Array.isArray(d.sections) || typeof d.savedAt !== 'string') return null;
    if (!(typeof d.baseUpdatedAt === 'string' || d.baseUpdatedAt === null)) return null;
    return { baseUpdatedAt: d.baseUpdatedAt, sections: d.sections, savedAt: d.savedAt };
  } catch {
    return null;
  }
}

/**
 * 'restore' = the draft was made on the version the server holds now and
 * differs from it; 'stale' = the page was saved since (the draft is dropped
 * with a note); 'same' = nothing to restore.
 */
export function draftStatus(
  draft: AboutDraft | null,
  serverUpdatedAt: string | null | undefined,
  serverSnapshot: string
): 'restore' | 'stale' | 'same' | 'none' {
  if (!draft) return 'none';
  if (snapshotOf(draft.sections) === serverSnapshot) return 'same';
  if ((serverUpdatedAt ?? null) !== draft.baseUpdatedAt) return 'stale';
  return 'restore';
}

/** "HH:MM" of the draft, local time. */
export function draftTime(draft: AboutDraft): string {
  const d = new Date(draft.savedAt);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
